import ReactDOM from 'react-dom/client';
import {
  CheckCircle,
  ExclamationTriangle,
  InfoCircle,
  XCircle,
  X,
} from 'react-bootstrap-icons';

const TOAST_CONFIG = {
  success: {
    accent: '#34C759',
    border: 'rgba(52, 199, 89, 0.22)',
    Icon: CheckCircle,
    label: 'Success',
  },
  error: {
    accent: '#FF3B30',
    border: 'rgba(255, 59, 48, 0.22)',
    Icon: XCircle,
    label: 'Error',
  },
  warning: {
    accent: '#FF9500',
    border: 'rgba(255, 149, 0, 0.22)',
    Icon: ExclamationTriangle,
    label: 'Warning',
  },
  info: {
    accent: '#007AFF',
    border: 'rgba(0, 122, 255, 0.22)',
    Icon: InfoCircle,
    label: 'Info',
  },
};

const getToastConfig = (type) => TOAST_CONFIG[type] || TOAST_CONFIG.info;

const createToastContainer = () => {
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.style.cssText = `
      position: fixed;
      top: 24px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 2147483647;
      display: flex;
      flex-direction: column;
      gap: 8px;
      pointer-events: none;
    `;
    document.body.appendChild(container);
  }
  return container;
};

const removeAllToasts = () => {
  const container = document.getElementById('toast-container');
  if (container) container.innerHTML = '';
};

const injectStyles = () => {
  if (document.getElementById('fineats-toast-styles')) return;
  const style = document.createElement('style');
  style.id = 'fineats-toast-styles';
  style.textContent = `
    @keyframes _toastIn {
      0%   { transform: translateY(-18px) scale(0.93); opacity: 0; filter: blur(4px); }
      55%  { transform: translateY(4px)   scale(1.012); opacity: 1; filter: blur(0); }
      80%  { transform: translateY(-1px)  scale(0.998); }
      100% { transform: translateY(0)     scale(1);     opacity: 1; }
    }
    @keyframes _toastOut {
      0%   { transform: scale(1)    translateY(0);    opacity: 1; filter: blur(0); }
      100% { transform: scale(0.91) translateY(-14px); opacity: 0; filter: blur(3px); }
    }
    @keyframes _progress {
      from { transform: scaleX(1); }
      to   { transform: scaleX(0); }
    }
    ._toast-enter { animation: _toastIn  0.46s cubic-bezier(0.34, 1.24, 0.64, 1) forwards; }
    ._toast-exit  { animation: _toastOut 0.26s cubic-bezier(0.4, 0, 1, 1) forwards; pointer-events: none; }
    ._toast-close { transition: background 0.14s ease, transform 0.1s ease; }
    ._toast-close:hover  { background: rgba(60,60,67,0.16) !important; }
    ._toast-close:active { background: rgba(60,60,67,0.24) !important; transform: scale(0.88); }
    @media (max-width: 500px) {
      #toast-container {
        top: 16px !important;
        left: 16px !important;
        right: 16px !important;
        width: auto !important;
        transform: none !important;
      }
    }
  `;
  document.head.appendChild(style);
};

const ToastComponent = ({ message, config, toastId, duration }) => {
  const { Icon, accent, border, label } = config;

  const dismiss = () => {
    const el = document.getElementById(toastId);
    if (!el) return;
    el.classList.remove('_toast-enter');
    el.classList.add('_toast-exit');
    setTimeout(() => el.remove(), 260);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        width: '340px',
        maxWidth: '100%',
        padding: '13px 13px 15px 15px',
        borderRadius: '16px',
        background: 'rgba(255, 255, 255, 0.93)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: `1px solid ${border}`,
        borderLeft: `3px solid ${accent}`,
        boxShadow: '0 2px 14px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif',
        position: 'relative',
        overflow: 'hidden',
        pointerEvents: 'all',
      }}
    >
      {/* Icon */}
      <div style={{ paddingTop: '1.5px', flexShrink: 0 }}>
        <Icon size={14} style={{ color: accent, display: 'block' }} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: '12.5px',
            fontWeight: 600,
            color: '#1C1C1E',
            letterSpacing: '-0.012em',
            lineHeight: 1.3,
            marginBottom: message ? '3px' : 0,
          }}
        >
          {label}
        </p>
        {message && (
          <p
            style={{
              margin: 0,
              fontSize: '12.5px',
              fontWeight: 400,
              color: '#48484A',
              lineHeight: 1.45,
              letterSpacing: '-0.008em',
              wordBreak: 'break-word',
            }}
          >
            {message}
          </p>
        )}
      </div>

      {/* Close */}
      <button
        className="_toast-close"
        onClick={dismiss}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          flexShrink: 0,
          marginTop: '1px',
          padding: 0,
          border: 'none',
          borderRadius: '50%',
          background: 'rgba(60, 60, 67, 0.10)',
          color: '#8E8E93',
          cursor: 'pointer',
        }}
      >
        <X size={10} />
      </button>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'rgba(0,0,0,0.04)',
        }}
      >
        <div
          style={{
            height: '100%',
            background: accent,
            opacity: 0.38,
            transformOrigin: 'left',
            animation: `_progress ${duration}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
};

const showToast = (message, type = 'info', duration = 4000) => {
  injectStyles();
  removeAllToasts();

  const container = createToastContainer();
  const toastId = `toast-${Date.now()}`;
  const config = getToastConfig(type);

  const wrapper = document.createElement('div');
  wrapper.id = toastId;
  wrapper.className = '_toast-enter';
  container.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  root.render(
    <ToastComponent
      message={message}
      config={config}
      toastId={toastId}
      duration={duration}
    />
  );

  setTimeout(() => {
    const el = document.getElementById(toastId);
    if (!el) return;
    el.classList.remove('_toast-enter');
    el.classList.add('_toast-exit');
    setTimeout(() => el.remove(), 260);
  }, duration);
};

export const toast = {
  success: (message, duration) => showToast(message, 'success', duration),
  error: (message, duration) => showToast(message, 'error', duration),
  info: (message, duration) => showToast(message, 'info', duration),
  warning: (message, duration) => showToast(message, 'warning', duration),
};

export default toast;
